define('core/models/Channel',[
    'underscore',
    'backbone',

    'core/UniqueModel',
    'core/api',
    'core/models/Forum',
    'core/strings',
], function (
    _,
    Backbone,

    UniqueModel,
    api,
    Forum,
    strings
) {
    'use strict';

    var gettext = strings.get;

    var Channel = Backbone.Model.extend({
        defaults: {
            primaryForum: {},
            slug: null,
            name: null,
            options: {},
            followUrl: 'channels/follow',
            unfollowUrl: 'channels/unfollow',
        },

        idAttribute: 'slug',

        initialize: function (_attributes, options) {
            this.buildPrimaryForum(options);
            this.listenTo(this, 'change:primaryForum', this.updatePrimaryForum);
            this.listenTo(this, 'change:primaryCategory', this.updatePrimaryCategory);
        },

        buildPrimaryForum: function () {
            if (this.primaryForum)
                return;

            var primaryForum = this.get('primaryForum');
            if (!primaryForum)
                return;

            this.primaryForum = new UniqueModel(Forum, primaryForum, { channel: this });

            // Don't want to store forum attributes in multiple places,
            // especially as they are unsynced and will diverge
            this.unset('primaryForum');
        },

        updatePrimaryForum: function () {
            var updated = this.get('primaryForum');
            if (!updated)
                return;

            if (!this.primaryForum)
                this.buildPrimaryForum();

            this.primaryForum.set(updated);

            this.unset('primaryForum');
        },

        /**
         * Creates or updates the related primaryCategory model (a Channel Model
         * set as a property directly on this, not in attributes).
         */
        updatePrimaryCategory: function () {
            var object = this.get('primaryCategory');
            var existing = this.primaryCategory;

            // To delete a related category, set it to null in attributes
            if (object === null)
                this.primaryCategory = undefined;
            else if (existing)
                existing.set(object);
            else
                this.primaryCategory = new UniqueModel(Channel, object);

            // Don't want to store attributes in multiple places, especially
            // as they are unsynced and will diverge. Can remove details
            // from attributes as they are now stored within the related
            // model.
            this.unset('primaryCategory');
            this.trigger('changeRelated:primaryCategory');
        },

        fetch: function (options) {
            options = options ? _.clone(options) : {};
            options.data = this.buildFetchData(options.data);
            return Backbone.Model.prototype.fetch.call(this, options);
        },

        buildFetchData: function (existing) {
            var data = existing ? _.clone(existing) : {};

            if (this.id)
                data.channel = this.id;

            return data;
        },

        url: function (type) {
            return api.getURL(this.constructor.URLS[type] || this.constructor.URLS.read);
        },

        sync: function (method, self, options) {
            var attrs = self.attributes;

            options = _.extend({
                url: this.url(method),

                // Must set emulateHTTP to true so that the request type will be POST instead
                // of PUT/DELETE/PATCH
                emulateHTTP: true,
            }, options);

            var defaultData = {
                bannerColor: attrs.bannerColor,
                description: attrs.description,

                // If related primaryCategory model isn't set, send empty to the server
                // by passing ''. The empty string turns into empty when converted
                // to FormData, and the server will set those fields to null.
                primaryCategory: self.primaryCategory && self.primaryCategory.get('slug') || '',
            };

            // Only set avatar if default is selected OR
            // file is select and user has uploaded a file
            //
            // NOTE: sending null for avatar will result
            // in an api error
            if (options.avatarType === 'default')
                defaultData.avatar = '';
            else if (attrs.avatar && !_.isString(attrs.avatar))
                defaultData.avatar = attrs.avatar;

            if (options.bannerType !== 'file')
                defaultData.banner = '';
            else if (attrs.banner && !_.isString(attrs.banner))
                defaultData.banner = attrs.banner;

            switch (method) {
            case 'create':
                // Set processData and contentType to false so data is sent as FormData
                options.processData = false;
                options.contentType = false;

                defaultData.name = attrs.name;
                options.data = this.toFormData(_.extend({}, defaultData, options.data));

                break;
            case 'update':
                // Set processData and contentType to false so data is sent as FormData
                options.processData = false;
                options.contentType = false;

                defaultData.channel = attrs.slug;
                options.data = this.toFormData(_.extend({}, defaultData, options.data));
            }

            return Backbone.sync(method, self, options);
        },

        toFormData: function (data) {
            return _.reduce(data, function (formData, value, key) {
                formData.append(key, _.isString(value) ? value.trim() : value);
                return formData;
            }, new window.FormData());
        },

        parse: function (response) {
            return response.response || response;
        },

        shouldFetch: function () {
            // If the channel was hard-coded, then the name will exist but dateAdded
            // will not, so we will need to fetch.
            return !this.get('name') || !this.get('dateAdded');
        },

        ensureFetched: function () {
            if (this.shouldFetch())
                this.fetch();
        },

        validate: function (attrs) {
            var validationErrors = [];

            var name = attrs.name.trim();

            if (name.length < this.constructor.MIN_NAME_LENGTH) {
                validationErrors.push({
                    attrName: 'name',
                    message: strings.interpolate(
                        gettext('Name must have at least %(minLength)s characters.'),
                        { minLength: this.constructor.MIN_NAME_LENGTH }),
                });
            } else if (name.length > this.constructor.MAX_NAME_LENGTH) {
                validationErrors.push({
                    attrName: 'name',
                    message: strings.interpolate(
                        gettext('Name must have less than %(maxLength)s characters.'),
                        { maxLength: this.constructor.MAX_NAME_LENGTH }),
                });
            }

            var description = attrs.description.trim();

            if (description.length < this.constructor.MIN_DESCRIPTION_LENGTH) {
                validationErrors.push({
                    attrName: 'description',
                    message: strings.interpolate(
                        gettext('Description must have at least %(minLength)s characters.'),
                        { minLength: this.constructor.MIN_DESCRIPTION_LENGTH }),
                });
            } else if (description.length > this.constructor.MAX_DESCRIPTION_LENGTH) {
                validationErrors.push({
                    attrName: 'description',
                    message: strings.interpolate(
                        gettext('Description must have less than %(maxLength)s characters.'),
                        { maxLength: this.constructor.MAX_DESCRIPTION_LENGTH }),
                });
            }

            if (!this.constructor.BANNER_COLORS[attrs.bannerColor]) {
                validationErrors.push({
                    attrName: 'bannerColor',
                    message: strings.interpolate(
                        gettext('Banner color must be one of ' + _.invoke(_.values(this.constructor.BANNER_COLORS), 'toLowerCase').join(', ')) + '.'
                    ),
                });
            }

            if (!_.isEmpty(validationErrors))
                return validationErrors;
        },

        _changeFollowingState: function (path, options) {
            options = options || {};
            options.type = 'POST';
            options.data = _.extend({
                target: this.get('slug'),
            }, options.data);

            return api.call(path, options);
        },

        follow: function (options) {
            this.primaryForum.set('isFollowing', true);

            return this._changeFollowingState(this.get('followUrl'), options);
        },

        unfollow: function (options) {
            this.primaryForum.set('isFollowing', false);

            return this._changeFollowingState(this.get('unfollowUrl'), options);
        },

        /**
         * Toggles the followed state on this Channel.
         *
         * NOTE: Only curation-only channels can be followed directly.
         * All normal channels should be followed via their primary forums.
         *
         * The followed state of this channel is stored and updated on the
         * primary forum model, even for a curation-only channel that is
         * followed directly. This is to remain consistent with normal
         * channels that follow their primary forum.
         *
         * @returns {Object} - The promise from making the api call to (un)follow,
         *                     or undefined if this channel cannot be followed
         *                     directly.
         */
        toggleFollowed: function () {
            // Can't follow a non-curation-only channel directly.
            // Can't determine current follow state without a primary forum.
            if (!this.get('options').isCurationOnlyChannel || !this.primaryForum)
                return;

            var retval = this.primaryForum.get('isFollowing') ?
                this.unfollow() :
                this.follow();

            this.primaryForum.trigger('toggled:isFollowing');

            return retval;
        },

        toJSON: function () {
            var json = Backbone.Model.prototype.toJSON.call(this);

            return _.defaults({
                primaryCategory: this.primaryCategory ? this.primaryCategory.toJSON() : {},
            }, json);
        },
    }, {
        URLS: {
            read: 'channels/details',
            create: 'channels/create',
            update: 'channels/update',
        },
        BANNER_COLORS: {
            gray: gettext('Gray'),
            blue: gettext('Blue'),
            green: gettext('Green'),
            yellow: gettext('Yellow'),
            orange: gettext('Orange'),
            red: gettext('Red'),
            purple: gettext('Purple'),
        },
        MIN_NAME_LENGTH: 3,
        MAX_NAME_LENGTH: 100,
        MIN_DESCRIPTION_LENGTH: 5,
        MAX_DESCRIPTION_LENGTH: 200,
    });

    UniqueModel.addType('Channel', Channel);

    return Channel;
});

